import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Wrench, Cloud, Fish, Key, Globe, Megaphone, Phone, ShoppingCart, ArrowsLeftRight, Translate, Monitor, ShareNetwork, ArrowUDownLeft, HardDrives } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../lib/auth";

const MODULE_ICONS = {
  shield: Shield, wrench: Wrench, cloud: Cloud, fish: Fish, key: Key,
  globe: Globe, megaphone: Megaphone, phone: Phone,
  "shopping-cart": ShoppingCart, "arrows-left-right": ArrowsLeftRight,
  translate: Translate, monitor: Monitor, "share-network": ShareNetwork, "arrow-u-down-left": ArrowUDownLeft, "hard-drives": HardDrives,
};

export default function Modules({ embedded = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/modules")
      .then((r) => setModules(r.data.modules || []))
      .catch((e) => toast.error("Impossible de charger les modules: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async (m, enabled) => {
    setSaving(m.id);
    try {
      await api.put(`/modules/${m.id}/enabled`, { enabled });
      setModules((prev) => prev.map((x) => (x.id === m.id ? { ...x, enabled, enabled_for_tech: enabled } : x)));
      window.dispatchEvent(new Event("module-flags-changed"));
      toast.success(enabled ? `« ${m.title} » activé` : `« ${m.title} » désactivé pour tout le monde`);
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(null); }
  };

  const toggleAccess = async (m, enabled) => {
    setSaving(m.id);
    try {
      await api.put(`/modules/${m.id}/access`, { enabled_for_tech: enabled });
      setModules((prev) => prev.map((x) => (x.id === m.id ? { ...x, enabled_for_tech: enabled } : x)));
      toast.success(enabled ? `« ${m.title} » activé pour les techniciens` : `« ${m.title} » réservé aux admins`);
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(null); }
  };

  return (
    <div className={embedded ? "p-6" : "p-6 max-w-5xl"}>
      {!embedded && (
        <header className="mb-8">
          <h1 className="text-3xl font-display font-bold" data-testid="modules-title">Modules</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isAdmin
              ? "Boîte à outils Netkom. Cochez « Accessible aux techniciens » pour qu'un module apparaisse dans leur menu."
              : "Outils mis à votre disposition par les administrateurs."}
          </p>
        </header>
      )}
      {embedded && (
        <p className="text-muted-foreground mb-6 text-sm">
          Boîte à outils Netkom. Cliquez sur un module pour l'ouvrir ; cochez « Accessible aux techniciens » pour qu'il apparaisse dans le menu des techs.
        </p>
      )}
      {loading ? (
        <div className="text-muted-foreground">Chargement…</div>
      ) : modules.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed p-10 text-center text-muted-foreground">
          Aucun module ne vous a été mis à disposition pour l'instant.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.map((m) => {
            const Icon = MODULE_ICONS[m.icon] || Shield;
            return (
              <div
                key={m.id}
                data-testid={`module-card-${m.id}`}
                className={`group text-left rounded-xl border bg-card hover:bg-accent hover:shadow-md transition-all p-5 flex flex-col gap-3 ${m.enabled === false ? "opacity-60" : ""}`}
              >
                <button
                  onClick={() => {
                    // v279 — Techs clicking the "Mur des urgences" card land
                    // directly on the live wall view (/mur) instead of the
                    // admin config page (/modules/wall) which is admin-only
                    // and would otherwise redirect them to the dashboard.
                    if (m.enabled === false) {
                      toast.info(`« ${m.title} » est désactivé — activez-le avec l'interrupteur de la carte.`);
                      return;
                    }
                    if (m.id === "wall" && !isAdmin) {
                      nav("/mur");
                      return;
                    }
                    nav(m.endpoint || `/modules/${m.id}`);
                  }}
                  className="text-left flex-1 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="rounded-lg bg-primary/10 p-2.5 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon size={22} weight="bold"/>
                    </div>
                    <span className={`text-[0.65rem] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${m.enabled === false ? "text-zinc-600 bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300" : "text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/40"}`}>
                      {m.enabled === false ? "désactivé" : m.status}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-lg">{m.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-3">{m.description}</p>
                </button>
                {isAdmin && m.global_toggle && (
                  <div className="pt-3 border-t flex items-center justify-between text-xs" data-testid={`module-enable-${m.id}`}>
                    <span className="text-muted-foreground">{m.enabled ? "Activé pour tout le monde" : "Désactivé"}</span>
                    <Switch
                      checked={!!m.enabled}
                      onCheckedChange={(v) => toggleEnabled(m, v)}
                      disabled={saving === m.id}
                      data-testid={`module-enable-switch-${m.id}`}
                    />
                  </div>
                )}
                {isAdmin && (!m.global_toggle || (m.tech_toggle && !m.tech_toggle_in_module)) && (
                  <div className="pt-3 border-t flex items-center justify-between text-xs" data-testid={`module-toggle-${m.id}`}>
                    <span className="text-muted-foreground">Accessible aux techniciens</span>
                    <Switch
                      checked={!!m.enabled_for_tech}
                      onCheckedChange={(v) => toggleAccess(m, v)}
                      disabled={saving === m.id}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div className="rounded-xl border-2 border-dashed p-5 flex flex-col items-center justify-center text-center text-muted-foreground gap-1 min-h-[160px]">
            <div className="text-3xl">✨</div>
            <div className="text-sm">Plus de modules bientôt</div>
            <div className="text-xs">Suggérez-en un dans « Signaler un bug »</div>
          </div>
        </div>
      )}
    </div>
  );
}
